import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import analyticsService from '../../../services/analyticsService';

interface ABTestVariant {
  name: string;
  isControl: boolean;
  subject?: string;
  content?: string;
  sender_name?: string;
  send_time?: string;
  distribution: number;
  sent_count: number;
  opened_count: number;
  opened_rate: number;
  clicked_count: number;
  clicked_rate: number;
  replied_count: number;
  replied_rate: number;
  converted_count?: number;
  conversion_rate?: number;
}

interface ABTestResult {
  id: string;
  campaign_id: string;
  campaign_name: string;
  test_type: 'subject' | 'content' | 'sender_name' | 'send_time';
  winner_metric: 'open_rate' | 'click_rate' | 'reply_rate' | 'conversion_rate';
  user_selected_variant: string | null;
  created_at: string;
  updated_at: string;
  variants: ABTestVariant[];
}

type MetricKey = 'opened_rate' | 'clicked_rate' | 'replied_rate' | 'conversion_rate';

interface ChartData {
  name: string;
  value: number;
  isControl: boolean;
  variantName: string;
}

interface ABTestResultsProps {
  selectedCampaign?: string; // Optional campaign ID to filter for
}

const metricLabels = {
  'open_rate': 'Open Rate',
  'click_rate': 'Click Rate',
  'reply_rate': 'Reply Rate',
  'conversion_rate': 'Conversion Rate'
};

// Helper function to safely get metric values from variants
const getCurrentMetricValue = (variant: ABTestVariant, metricType: string): number => {
  const metricKey = `${metricType.split('_')[0]}_rate` as keyof ABTestVariant;
  // Type guard to check if the key exists and is a number
  if (metricKey in variant) {
    const value = variant[metricKey];
    if (typeof value === 'number') {
      return parseFloat((value * 100).toFixed(1));
    }
  }
  return 0;
};

const ABTestResults: React.FC<ABTestResultsProps> = ({ selectedCampaign }) => {
  const [testResults, setTestResults] = useState<ABTestResult[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTest, setSelectedTest] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchABTests = async () => {
      try {
        setLoading(true);
        const results = await analyticsService.getABTestResults();
        setTestResults(results as ABTestResult[]);
        
        // Select first test by default if available
        if (results.length > 0 && !selectedTest) {
          setSelectedTest(results[0].campaign_id);
        }
        
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load A/B test results');
        console.error('Error loading A/B test results:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchABTests();
  }, [selectedCampaign]);

  const handleSelectWinner = async (campaignId: string, variantName: string) => {
    try {
      await analyticsService.selectABTestWinner(campaignId, variantName);
      
      // Update local state to reflect the selection
      setTestResults(prevResults => 
        prevResults.map(test => 
          test.campaign_id === campaignId 
            ? { ...test, user_selected_variant: variantName } 
            : test
        )
      );
      
      setSuccessMessage(`Successfully selected "${variantName}" as the winner!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to select winner');
      console.error('Error selecting winner:', err);
    }
  };

  const getCurrentTest = () => {
    if (!selectedTest) return null;
    return testResults.find(test => test.campaign_id === selectedTest) || null;
  };

  // Format data for chart display
  const getChartData = (test: ABTestResult) => {
    if (!test) return [];
    
    const metricKey = `${test.winner_metric.split('_')[0]}_rate` as keyof ABTestVariant;
    
    return test.variants.map(variant => ({
      name: variant.name,
      [test.winner_metric]: parseFloat((variant[metricKey] as number * 100).toFixed(2)),
      isControl: variant.isControl,
      isWinner: variant.name === test.user_selected_variant
    }));
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded mb-4" role="alert">
        <div className="flex justify-between items-center">
          <span>{error}</span>
          <button 
            className="ml-4 bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded text-sm"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (testResults.length === 0) {
    return (
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-700">No A/B Tests Available</h3>
        <p className="text-gray-500">
          Create a campaign with A/B testing enabled to start testing different variants.
        </p>
      </div>
    );
  }

  const currentTest = getCurrentTest();

  return (
    <div className="w-full space-y-6">
      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg flex justify-between items-center">
        <div>
          <h4 className="font-medium">A/B Test Results Dashboard</h4>
          <p className="text-sm">Here you can view results of your A/B tests and select winning variants based on performance.</p>
        </div>
        <a href="/email/campaigns/new" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm">
          Create New A/B Test
        </a>
      </div>
      
      {successMessage && (
        <div className="bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded mb-4" role="alert">
          <span>{successMessage}</span>
        </div>
      )}
      
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">A/B Test Results</h3>
          
          <select
            value={selectedTest || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTest(e.target.value)}
            className="min-w-[200px] mt-2 md:mt-0 p-2 border border-gray-300 rounded-md"
          >
            <option value="" disabled>Select Campaign</option>
            {testResults.map((test) => (
              <option key={test.campaign_id} value={test.campaign_id}>
                {test.campaign_name}
              </option>
            ))}
          </select>
        </div>
        
        {currentTest && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4 text-sm">
              <div className="bg-blue-50 px-3 py-1 rounded-full text-blue-700">
                Test Type: {currentTest.test_type.replace('_', ' ')}
              </div>
              <div className="bg-green-50 px-3 py-1 rounded-full text-green-700">
                Winner Metric: {metricLabels[currentTest.winner_metric]}
              </div>
              {currentTest.user_selected_variant && (
                <div className="bg-purple-50 px-3 py-1 rounded-full text-purple-700">
                  Selected Winner: {currentTest.user_selected_variant}
                </div>
              )}
            </div>
            
            {/* Results Chart */}
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={getChartData(currentTest)}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: `${metricLabels[currentTest.winner_metric]} (%)`, angle: -90, position: 'insideLeft' }} />
                  <Tooltip formatter={(value) => [`${value}%`, metricLabels[currentTest.winner_metric]]} />
                  <Legend />
                  <Bar 
                    dataKey={currentTest.winner_metric} 
                    fill="#8884d8" 
                    background={{ fill: '#eee' }}
                    barSize={35}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Variants Detail */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-700">Test Variants</h4>
              
              {currentTest.variants.map((variant) => (
                <div key={variant.name} className="p-4 border border-gray-200 rounded-lg shadow-sm bg-white">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="font-medium text-gray-800">{variant.name}</h5>
                        {variant.isControl && (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">Control</span>
                        )}
                        {variant.name === currentTest.user_selected_variant && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-600 rounded">Selected Winner</span>
                        )}
                      </div>
                      
                      {variant.subject && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Subject:</span> {variant.subject}
                        </p>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                        <div>
                          <p className="text-xs text-gray-500">Sent</p>
                          <p className="font-medium">{variant.sent_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Opened</p>
                          <p className="font-medium">{variant.opened_count} ({(variant.opened_rate * 100).toFixed(1)}%)</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Clicked</p>
                          <p className="font-medium">{variant.clicked_count} ({(variant.clicked_rate * 100).toFixed(1)}%)</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Replied</p>
                          <p className="font-medium">{variant.replied_count} ({(variant.replied_rate * 100).toFixed(1)}%)</p>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-1">
                          {metricLabels[currentTest.winner_metric]}
                        </p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500" 
                              style={{ 
                                width: `${getCurrentMetricValue(variant, currentTest.winner_metric)}%`
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium">
                            {getCurrentMetricValue(variant, currentTest.winner_metric)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {!currentTest.user_selected_variant && (
                      <div className="flex items-start">
                        <button
                          className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded text-sm"
                          onClick={() => handleSelectWinner(currentTest.campaign_id, variant.name)}
                        >
                          Select as Winner
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Help Text */}
            {!currentTest.user_selected_variant && (
              <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">How to Select a Winner</p>
                <p>
                  Compare the variants based on the chosen winner metric ({metricLabels[currentTest.winner_metric]}).
                  Once you select a winning variant, future campaigns can use that variant as your default.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ABTestResults;
